import {Amplify} from "aws-amplify";

import {AuthRequestProps, sendAuthenticatedRequest} from "./api/Requests";
import {
  AssetPayloadType,
  AssetType,
  CategoryParentType,
  CategoryPostResponseType,
  CategoryType,
  CommentType,
  CreateQuest,
  CreateReward,
  NotificationType,
  PlayMakersConfig,
  ProjectConfigType,
  ProjectPayloadType,
  ProjectPushPayloadType,
  ProjectType,
  Quest,
  QuestType,
  QuestWithQuestType,
  Reward,
  RewardType,
  SchemaPayloadType,
  SchemaType,
  SelfUser,
  SubmissionType,
  SubmissionVoteGetResponseType,
  UpdateQuest,
  UpdateReward,
  User,
  UserCreateParentResponse,
  UserLinkFederatedResponse,
  UserQuest,
  UserReward,
  UserUpdateAvatarResponse,
  VoteGetResponseType,
  VotePostResponseType} from "./types";

class Api {
  baseUrl;
  cacheExpiration;
  uploadsUrl;
  projectId;
  twitterSignInUrl;

  constructor(config: PlayMakersConfig) {
    this.baseUrl = config.API_BASEURL;
    this.cacheExpiration = config.API_CACHE_EXPIRATION;
    this.uploadsUrl = config.UPLOADS_BASEURL;
    this.projectId = config.PROJECT_ID;
    const redirectUrlParam = encodeURIComponent(config.TWITTER.CALLBACK_URL);
    this.twitterSignInUrl = `${this.baseUrl}/auth/redirect/twitter?redirectUrl=${redirectUrlParam}`;
    Amplify.configure({
      Auth: {
        Cognito: {
          loginWith: {
            oauth: {
              domain: config.COGNITO.DOMAIN,
              redirectSignIn: config.COGNITO.REDIRECT_SIGNIN,
              redirectSignOut: config.COGNITO.REDIRECT_SIGNOUT,
              responseType: "code",
              // Amplify Bug as of 202405- scope is fixed as aws.cognito.signin.user.admin
              // for all users. We therefore ignore scope for authorization.
              // https://github.com/aws-amplify/amplify-js/issues/3732
              scopes: ["aws.cognito.signin.user.admin", "email", "profile", "openid"]
            },
            username: true
          },
          userPoolClientId: config.COGNITO.CLIENT_ID,
          userPoolId: config.COGNITO.POOL_ID
        }
      }
    });
  }

  call = async<T = void>({url, ...rest}: AuthRequestProps) => await sendAuthenticatedRequest<T>({
    cacheExpiration: this.cacheExpiration,
    url: `${this.baseUrl}/${url}`,
    ...rest
  });

  exports = [
    "baristaRequest",
    "createAsset",
    "updateAsset",
    "updateAssetFile",
    "getAsset",
    "getGlobalAssets",
    "getProjectAssets",
    "getSchemaAssets",
    "deleteAsset",
    "getCategories",
    "getParentCategories",
    "createCategory",
    "deleteCategory",
    "renameCategory",
    "getUserComments",
    "getSubmissionComments",
    "getComment",
    "createComment",
    "updateComment",
    "deleteComment",
    "getProjects",
    "getProject",
    "createProject",
    "updateProject",
    "updateProjectConfig",
    "updateProjectThumbnail",
    "createSchema",
    "updateSchema",
    "deleteSchema",
    "getSchemas",
    "getSchemasByCategory",
    "getSchema",
    "updateSchemaThumbnail",
    "createSubmission",
    "updateSubmission",
    "deleteSubmission",
    "getSubmission",
    "getSubmissions",
    "getSubmissionsBySchema",
    "getSubmissionsByIds",
    "updateSubmissionsStates",
    "signUp",
    "signUpConfirmation",
    "resendConfirmationCode",
    "sendPasswordResetCode",
    "passwordReset",
    "getMe",
    "getUser",
    "getUserMetadata",
    "getTokensFromTwitterToken",
    "getCreators",
    "updateAvatar",
    "createUserParent",
    "linkFederated",
    "unlinkFederated",
    "vote",
    "downVote",
    "upVote",
    "cancelVote",
    "getVote",
    "voteBalance",
    "getNotifications",
    "markAllNotificationsAsRead",
    "updateNotification",
    "deleteNotification",
    "getTags",
    "redirectTwitterLogin",
    "getSteamProfile",
    "getQuestType",
    "getQuestTypes",
    "getQuestTypeCategories",
    "getQuests",
    "getQuest",
    "createQuest",
    "deleteQuest",
    "updateQuest",
    "getUserQuest",
    "getUserQuests",
    "getUserQuestsByProject",
    "getUserQuestsByQuest",
    "completeQuest",
    "getRewardTypes",
    "getRewardType",
    "getRewardsByProject",
    "getReward",
    "createReward",
    "updateReward",
    "deleteReward",
    "redeemReward",
    "getUserRewards",
    "getUserRewardsByProject",
    "getUserRewardsByReward",
    "getUserReward",
    "updateUserRewardState"
  ];

  // ------------- HELPERS -------------

  extendSubmission = (submission: SubmissionType): SubmissionType => {
    if(!submission.zip) submission.zip = `${this.uploadsUrl}/${submission.id}/submission.zip`;
    return submission;
  };

  // ------------- MISC -------------

  baristaRequest = async({schema, submission}: any): Promise<boolean> =>
    await this.call<boolean>({
      method: "post",
      params: {schema, submission},
      url: "barista"
    });

  // ------------- ASSET -------------

  createAsset = async(params: Partial<AssetPayloadType>): Promise<AssetType> =>
    await this.call<AssetType>({
      method: "post",
      params,
      url: "asset"
    });

  updateAsset = async(assetId: string, params: Partial<AssetPayloadType>): Promise<AssetType> =>
    await this.call<AssetType>({
      method: "put",
      params,
      url: `asset/${assetId}`
    });

  updateAssetFile = async(assetId: string, file: File): Promise<AssetType> =>
    await this.call<AssetType>({
      files: [file],
      method: "put",
      url: `asset/${assetId}/upload`
    });

  getAsset = async(assetId: string): Promise<AssetType> =>
    await this.call<AssetType>({
      method: "get",
      url: `asset/${assetId}`
    });

  // params can have `type` and `tags`
  getGlobalAssets = async(params: {[key: string]: unknown}): Promise<AssetType[]> =>
    await this.call<AssetType[]>({
      method: "get",
      params,
      url: "assets/global"
    });

  // params can have `type` and `tags`
  getProjectAssets =
    async(params: {[key: string]: unknown}, projectId: string = this.projectId): Promise<AssetType[]> =>
      await this.call<AssetType[]>({
        method: "get",
        params,
        url: `assets/by/project/${projectId}`
      });

  // params can have `type` and `matchTags` <-- boolean if true looks into project and global
  getSchemaAssets = async(schemaId: string, params: {[key: string]: unknown} = {}): Promise<AssetType[]> =>
    await this.call<AssetType[]>({
      method: "get",
      params,
      url: `assets/by/schema/${schemaId}`
    });

  deleteAsset = async(assetId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `asset/${assetId}`
    });


  // ------------- CATEGORY -------------

  getCategories = async(
    parentId?: string, projectId: string = this.projectId, params:{[key: string]: unknown} = {}
  ): Promise<CategoryType[]> =>
    await this.call<CategoryType[]>({
      method: "get",
      params: {categoryId: parentId, ...params},
      url: `categories/${projectId}`
    });

  getParentCategories = async(categoryId: string): Promise<CategoryParentType[]> =>
    await this.call<CategoryParentType[]>({
      method: "get",
      url: `category/${categoryId}`
    });

  createCategory = async(
    name: string, parentId?: string, projectId: string = this.projectId
  ): Promise<CategoryPostResponseType> =>
    await this.call<CategoryPostResponseType>({
      method: "post",
      params: {name, parentId, projectId},
      url: "category"
    });

  deleteCategory = async(categoryId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `category/${categoryId}`
    });

  renameCategory = async(categoryId: string, name: string): Promise<CategoryPostResponseType> =>
    await this.call<CategoryPostResponseType>({
      method: "put",
      params: {name},
      url: `category/${categoryId}`
    });

  // ------------- COMMENT -------------

  // Get all comments written by a given user
  getUserComments = async(owner: string, params: {[key:string]: unknown}): Promise<CommentType[]> =>
    await this.call<CommentType[]>({
      method: "get",
      params,
      url: `comments/by/user/${owner}`
    });

  // Get all comments linked to a given submission
  getSubmissionComments = async(submissionId: string, params: {[key:string]: unknown}): Promise<CommentType[]> =>
    await this.call<CommentType[]>({
      method: "get",
      params,
      url: `comments/${submissionId}`
    });

  // Get a comment given its id
  getComment = async(id: string): Promise<CommentType> =>
    await this.call<CommentType>({
      method: "get",
      url: `comment/${id}`
    });

  // Create a new comment
  createComment = async(submissionId: string, body?: string, state?: string): Promise<CommentType> =>
    await this.call<CommentType>({
      method: "post",
      params: {body, state},
      url: `comment/${submissionId}`
    });

  // Update an existing comment
  updateComment = async(id:string, body?: string, state?: string): Promise<CommentType> =>
    await this.call<CommentType>({
      method: "put",
      params: {body, state},
      url: `comment/${id}`
    });

  // Delete an existing comment
  deleteComment = async(id:string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `comment/${id}`
    });

  // ------------- PROJECT -------------

  getProjects = async(params: {[key: string]: unknown} = {}): Promise<ProjectType[]> =>
    await this.call<ProjectType[]>({
      method: "get",
      params,
      url: "projects"
    });

  getProject = async(projectId: string = this.projectId): Promise<ProjectType> =>
    await this.call<ProjectType>({
      method: "get",
      url: `project/${projectId}`
    });

  createProject = async(projectParams: ProjectPayloadType): Promise<ProjectType> =>
    await this.call<ProjectType>({
      method: "post",
      params: projectParams,
      url: "project"
    });

  updateProject =
    async(params: Partial<ProjectPushPayloadType>, projectId: string = this.projectId): Promise<ProjectType> =>
      await this.call<ProjectType>({
        method: "put",
        params,
        url: `project/${projectId}`
      });

  updateProjectConfig = async(config: Partial<ProjectConfigType>, projectId: string = this.projectId): Promise<void> =>
    await this.call<void>({
      method: "put",
      params: config,
      url: `project/${projectId}/config`
    });


  updateProjectThumbnail = async(file: File, projectId: string = this.projectId): Promise<ProjectType> =>
    await this.call<ProjectType>({
      files: [file],
      method: "put",
      url: `project/${projectId}/thumbnail`
    });

  // ------------- SCHEMA -------------

  createSchema = async(params: SchemaPayloadType): Promise<SchemaType> =>
    await this.call<SchemaType>({
      method: "post",
      params,
      url: "schema"
    });

  updateSchema = async(schemaId: string, params: Partial<SchemaPayloadType>): Promise<SchemaType> =>
    await this.call<SchemaType>({
      method: "put",
      params,
      url: `schema/${schemaId}`
    });

  getSchemas =
    async(projectId: string = this.projectId, params: {[key: string]: unknown} = {}): Promise<SchemaType[]> =>
      await this.call<SchemaType[]>({
        method: "get",
        params,
        url: `schemas/by/project/${projectId}`
      });

  // TODO Is this needed ? Could have category as param of above
  getSchemasByCategory = async(categoryId: string): Promise<SchemaType[]> =>
    await this.call<SchemaType[]>({
      method: "get",
      url: `schemas/by/category/${categoryId}`
    });

  getSchema = async(schemaId: string): Promise<SchemaType> =>
    await this.call<SchemaType>({
      method: "get",
      url: `schema/${schemaId}`
    });

  deleteSchema = async(schemaId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `schema/${schemaId}`
    });

  updateSchemaThumbnail = async(schemaId: string, file: File): Promise<SchemaType> =>
    await this.call<SchemaType>({
      files: [file],
      method: "put",
      url: `schema/${schemaId}/thumbnail`
    });

  // ------------- SUBMISSION -------------

  createSubmission = async(params: {[key: string]: unknown} = {}, files: File[] = []): Promise<SubmissionType> =>
    this.extendSubmission(await this.call<SubmissionType>({
      files,
      method: "post",
      params,
      url: "submission"
    }));

  updateSubmission =
    async(submissionId: string, params: {[key: string]: unknown} = {}, files: File[] = []): Promise<SubmissionType> =>
      this.extendSubmission(await this.call<SubmissionType>({
        files,
        method: "put",
        params,
        url: `submission/${submissionId}`
      }));

  getSubmission = async(submissionId: string): Promise<SubmissionType> =>
    this.extendSubmission(await this.call<SubmissionType>({
      method: "get",
      url: `submission/${submissionId}`
    }));

  deleteSubmission = async(submissionId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `submission/${submissionId}`
    });

  getSubmissions = async(params: {[key: string]: unknown} = {}): Promise<SubmissionType[]> => {
    const submissions = await this.call<SubmissionType[]>({
      method: "get",
      params,
      url: "submissions"
    });
    return submissions.map(x => this.extendSubmission(x));
  };


  getSubmissionsBySchema = async(schemaId: string): Promise<SubmissionType[]> => {
    const submissions = await this.call<SubmissionType[]>({
      method: "get",
      params: {schemaId},
      url: `submissions/by/schema/${schemaId}`
    });
    return submissions.map(x => this.extendSubmission(x));
  };

  getSubmissionsByIds = async(submissionIds: string[]): Promise<SubmissionType[]> => {
    const submissions = await this.call<SubmissionType[]>({
      method: "get",
      params: {submissionIds},
      url: "submissions/by/ids"
    });
    return submissions.map(x => this.extendSubmission(x));
  };

  // TODO state should be of SubmissionState type
  updateSubmissionsStates = async(submissionIds: string[], state: string): Promise<void> =>
    await this.call<void>({
      method: "put",
      params: {state, submissionIds},
      url: "submissions/by/ids"
    });

  // ------------- USER -------------

  // Signup non-confirmed new user, receives code (via email)
  signUp = async(email: string, username: string, password: string): Promise<void> =>
    await this.call<void>({
      method: "post",
      params: {email, password, username},
      url: "user/auth/signup"
    });

  // Resend confirmation code for non-confirmed user (via email or username)
  resendConfirmationCode = async(email: string | null, username: string | null): Promise<void> => {
    if(!email && !username) throw new Error("Either email or username must be provided.");

    const params = {...email && {email}, ...username && {username}};

    return await this.call<void>({
      method: "post",
      params,
      url: "user/auth/resendConfirmation"
    });
  };

  // User requests code to reset password (via email)
  sendPasswordResetCode = async(email: string): Promise<void> =>
    await this.call<void>({
      method: "post",
      params: {email},
      url: "user/auth/sendPasswordResetCode"
    });

  // User resets password after receiving code
  passwordReset = async(username: string, code: string, password: string): Promise<void> =>
    await this.call<void>({
      method: "post",
      params: {code, password, username},
      url: "user/auth/passwordReset"
    });

  // Confirm user signup after receiving code
  signUpConfirmation = async(username: string | null, code: string, email: string | null): Promise<void> => {
    const params = {...email && {email}, ...username && {username}, confirmationCode: code};

    return await this.call<void>({
      method: "post",
      params,
      url: "user/auth/signupConfirmation"
    });
  };

  // Lookup current user info
  getMe = async(): Promise<SelfUser> => {
    try {
      const userData = await this.call<SelfUser>({
        method: "get",
        url: "user/auth/me"
      });
      return userData;
    } catch (error) {
      throw new Error("Failed to get user info.");
    }
  };

  // Get user info from the backend
  getUser = async(userId: string): Promise<User> =>
    await this.call<User>({
      method: "get",
      url: `user/${userId}`
    });

  // Get user info from the backend
  getUserMetadata = async(idToken: string): Promise<any[]> => {
    const response: any[] = await this.call({
      method: "post",
      params: {idToken},
      url: "user/refreshMetadata"
    });
    return response;
  };

  // Exchange Twitter token for PlayMakers tokens
  getTokensFromTwitterToken = async(accessToken: string, accessTokenSecret: string): Promise<unknown> =>
    await this.call({
      method: "post",
      params: {accessToken, accessTokenSecret},
      url: "user/auth/exchangeTwitterToken"
    });

  // Get last 5 creators
  getCreators = async(): Promise<User[]> =>
    await this.call<User[]>({
      method: "get",
      url: "creators"
    });

  updateAvatar = async(file: File): Promise<UserUpdateAvatarResponse> =>
    await this.call<UserUpdateAvatarResponse>({
      files: [file],
      method: "put",
      url: "user"
    });

  // Create a new parent account for an unlinked federated user, and link the two
  createUserParent = async(username: string, provider: string): Promise<UserCreateParentResponse> =>
    await this.call<UserCreateParentResponse>({
      method: "post",
      params: {provider, username},
      url: "user/federated/createParent"
    });

  // Link an existing account to an unlinked federated user, by access tokens
  linkFederated = async(accessToken: string, provider: string): Promise<UserLinkFederatedResponse> =>
    await this.call<UserLinkFederatedResponse>({
      method: "post",
      params: {accessToken, provider},
      url: "user/federated/linkFederated"
    });

  // Unlink a federated account, delete it
  unlinkFederated = async(sourceUsername: string, provider: string): Promise<SelfUser> =>
    await this.call<SelfUser>({
      method: "post",
      params: {provider, sourceUsername},
      url: "user/federated/unlinkFederated"
    });


  // ------------- VOTE -------------

  // Vote for a submission: upvote or downvote
  // TODO Deprecate ?
  vote = async(submissionId: string, direction: string): Promise<VotePostResponseType> =>
    await this.call<VotePostResponseType>({
      method: "post",
      url: `${direction === "up" ? "vote/up" : "vote/down"}/${submissionId}`
    });

  // Vote for a submission: downvote
  downVote = async(submissionId: string): Promise<VotePostResponseType> =>
    await this.call<VotePostResponseType>({
      method: "post",
      url: `vote/down/${submissionId}`
    });

  // Vote for a submission: upvote
  upVote = async(submissionId: string): Promise<VotePostResponseType> =>
    await this.call({
      method: "post",
      url: `vote/up/${submissionId}`
    });

  // Cancel one's vote
  cancelVote = async(submissionId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `vote/cancel/${submissionId}`
    });

  // Get current voting status
  getVote = async(submissionId: string): Promise<number> => {
    const vote = await this.call<VoteGetResponseType>({
      method: "get",
      url: `vote/${submissionId}`
    });
    return vote?.value || 0;
  };

  // Get voting balance for a given submission
  voteBalance = async(submissionId: string): Promise<number> => {
    const balance = await this.call<SubmissionVoteGetResponseType>({
      method: "get",
      url: `vote/balance/${submissionId}`
    });
    return balance?.balance || 0;
  };


  // ------------- NOTIFICATION -------------

  getNotifications = async(params: {[key: string]: unknown} = {}): Promise<NotificationType[]> =>
    await this.call<NotificationType[]>({
      method: "get",
      params,
      url: "notifications"
    });

  markAllNotificationsAsRead = async(): Promise<void> =>
    await this.call<void>({
      method: "put",
      url: "notifications"
    });

  updateNotification =
    async(notificationId: string, params: {[key: string]: unknown} = {}): Promise<NotificationType> =>
      await this.call<NotificationType>({
        method: "put",
        params,
        url: `notification/${notificationId}`
      });

  deleteNotification = async(notificationId: string): Promise<void> =>
    await this.call<void>({
      method: "delete",
      url: `notification/${notificationId}`
    });


  // ------------- TAG -------------

  getTags = async(projectId: string = this.projectId, params: {[key: string]: unknown} = {}): Promise<string[]> =>
    await this.call<string[]>({
      method: "get",
      params,
      url: `tags/by/project/${projectId}`
    });

  // ------------- TWITTER REDIRECTS -------------

  // Twitter endpoints for redirect
  // TODO replace with ID server

  redirectTwitterLogin = () => {
    window.location.replace(this.twitterSignInUrl);
  };

  // --------------- QUESTS TYPES ----------------
  // Quest types are the different types of quests available
  // They are used to create quests

  getSteamProfile = async() =>
    await this.call<unknown>({
      method: "get",
      url: "quest/api/fetch/steam?operation=profile"
    });

  getQuestType = async(questTypeId: string) =>
    await this.call<QuestType>({
      method: "get",
      url: `questTypes/${questTypeId}`
    });

  getQuestTypes = async(params: {[key: string]: any} = {}) =>
    await this.call<QuestType[]>({
      method: "get",
      params,
      url: "questTypes"
    });

  getQuestTypeCategories = async(params: {[key: string]: any} = {}) =>
    await this.call<string[]>({
      method: "get",
      params,
      url: "questTypes/category"
    });

  // --------------- QUESTS ----------------
  // All quests available

  getQuest = async(questId: string) =>
    await this.call<Quest>({
      method: "get",
      url: `quest/${questId}`
    });

  getQuests = async(params: {[key: string]: any}, projectId: string = this.projectId) =>
    await this.call<QuestWithQuestType[]>({
      method: "get",
      params,
      url: `quests/by/project/${projectId}`
    });

  getUserQuests = async(params: {[key: string]: any} = {}, projectId: string = this.projectId) =>
    await this.call<UserQuest[]>({
      method: "get",
      params,
      url: `userQuests/by/project/${projectId}`
    });

  completeQuest = async(questId: string, creatorData?: Record<string, unknown>) =>
    await this.call<UserQuest>({
      method: "post",
      params: creatorData,
      url: `quest/${questId}/complete`
    });

  createQuest = async(params: CreateQuest) =>
    await this.call<Quest>({
      method: "post",
      params,
      url: "quest"
    });

  updateQuest = async(questId: string, params: UpdateQuest) =>
    await this.call<Quest>({
      method: "put",
      params,
      url: `quest/${questId}`
    });

  deleteQuest = async(questId: string) =>
    await this.call({
      method: "delete",
      url: `quest/${questId}`
    });

  // --------------- USER QUESTS ----------------
  // Quests that the current user has completed

  getUserQuest = async(userQuestId: string) =>
    await this.call<UserQuest>({
      method: "get",
      url: `userQuest/${userQuestId}`
    });

  getUserQuestsByQuest = async(params: {[key: string]: any} = {}, questId: string) =>
    await this.call<UserQuest[]>({
      method: "get",
      params,
      url: `userQuests/by/quest/${questId}`
    });

  getUserQuestsByProject = async(params: {[key: string]: any} = {}, projectId: string = this.projectId) =>
    await this.call<UserQuest[]>({
      method: "get",
      params,
      url: `userQuests/by/project/${projectId}`
    });

  // --------------- REWARDS ----------------
  getRewardTypes = async(params: {[key: string]: any} = {}) =>
    await this.call<RewardType[]>({
      method: "get",
      params,
      url: "rewardTypes"
    });

  getRewardType = async(rewardTypeId: string) =>
    await this.call<RewardType>({
      method: "get",
      url: `rewardType/${rewardTypeId}`
    });

  getRewardsByProject = async(params: {[key: string]: any}, projectId: string = this.projectId) =>
    await this.call<Reward[]>({
      method: "get",
      params,
      url: `rewards/by/project/${projectId}`
    });

  getReward = async(rewardId: string) =>
    await this.call<Reward>({
      method: "get",
      url: `reward/${rewardId}`
    });

  redeemReward = async(rewardId: string) =>
    await this.call<UserReward>({
      method: "post",
      url: `reward/${rewardId}/redeem`
    });

  createReward = async(reward: CreateReward) =>
    await this.call<Reward>({
      method: "post",
      params: reward,
      url: "reward"
    });

  updateReward = async(rewardId: string, reward: UpdateReward) =>
    await this.call<Reward>({
      method: "put",
      params: reward,
      url: `reward/${rewardId}`
    });

  deleteReward = async(rewardId: string) =>
    await this.call({
      method: "delete",
      url: `reward/${rewardId}`
    });

  // --------------- USER REWARDS ----------------
  // Rewards for the current user

  getUserRewards = async(params: {[key: string]: any} = {}) =>
    await this.call<UserReward[]>({
      method: "get",
      params,
      url: "userRewards"
    });

  getUserRewardsByProject = async(params: {[key: string]: any} = {}, projectId: string = this.projectId) =>
    await this.call<UserReward[]>({
      method: "get",
      params,
      url: `userRewards/by/project/${projectId}`
    });

  getUserRewardsByReward = async(rewardId: string, params: {[key: string]: any} = {}) =>
    await this.call<UserReward[]>({
      method: "get",
      params,
      url: `userRewards/by/reward/${rewardId}`
    });

  getUserReward = async(userRewardId: string) =>
    await this.call<UserReward>({
      method: "get",
      url: `userReward/${userRewardId}`
    });

  // Update userReward state (pending --> redemeed, redeemed --> cancel)
  updateUserRewardState = async(userRewardId: string, state: string) =>
    await this.call<UserReward>({
      method: "put",
      params: {state},
      url: `userReward/${userRewardId}`
    });

}


export {Api};
